import { useState } from "react";
import { toast } from "sonner";
import { Camera, CheckCircle2, AlertCircle, Clock, ImagePlus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/format";
import { api, apiError } from "@/lib/api";

const STATUS = {
  pendente: { label: "Aguardando", icon: Clock, cls: "bg-gold-soft text-gold-dark border-gold/30" },
  aprovada: { label: "Aprovada", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  ajuste: { label: "Ajuste pedido", icon: AlertCircle, cls: "bg-red-50 text-red-700 border-red-200" },
};

export const compress = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error("Formato de imagem não suportado. Tire a foto novamente ou escolha uma imagem JPG ou PNG."));
    };
    img.onload = () => {
      const s = Math.min(1, 800 / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = img.width * s;
      c.height = img.height * s;
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(img.src);
      resolve(c.toDataURL("image/jpeg", 0.7));
    };
    img.src = URL.createObjectURL(file);
  });

export const uploadPhoto = async (file, companyId) => {
  const data = await compress(file);
  const { data: r } = await api.post("/photos", { data, companyId });
  return r.url;
};

export function usePhotoPicker(initial, companyId) {
  const [photo, setPhoto] = useState(initial || null);
  const [uploading, setUploading] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      setPhoto(await uploadPhoto(file, companyId));
    } catch (err) {
      toast.error(err.response ? apiError(err) : err.message);
    } finally {
      setUploading(false);
    }
  };
  return { photo, uploading, pick };
}

export const PhotoPreview = ({ photo, uploading, label }) =>
  uploading ? <span className="flex flex-col items-center gap-2 text-xs font-medium text-gold-dark" data-testid="photo-uploading"><Loader2 className="h-5 w-5 animate-spin" /> Enviando foto...</span>
  : photo ? <img src={photo} alt="Foto" className="h-full w-full object-cover" />
  : <span className="flex flex-col items-center gap-2 text-xs font-medium text-gold-dark"><ImagePlus className="h-5 w-5" /> {label}</span>;

export function ProofForm({ initial, onSave, testId, requirePhoto, companyId }) {
  const { photo, uploading, pick } = usePhotoPicker(initial?.photo, companyId);
  const [note, setNote] = useState(initial?.note || "");
  const save = () => {
    if (requirePhoto && !photo) return toast.error("Atividade operacional: a foto é obrigatória.");
    if (!photo && !note.trim()) return toast.error("Adicione uma foto ou uma observação.");
    onSave({ photo, note: note.trim() });
  };
  return (
    <div className="space-y-4">
      <label className="flex h-48 cursor-pointer items-center justify-center overflow-hidden rounded-xl border border-dashed border-gold bg-gold-soft/40 transition-colors hover:bg-gold-soft" data-testid={`${testId}-photo-label`}>
        <PhotoPreview photo={photo} uploading={uploading} label="Tirar ou escolher foto" />
        <input type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid={`${testId}-photo-input`} />
      </label>
      <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Observação (ex.: vitrine organizada às 9h)" data-testid={`${testId}-note-input`} />
      <Button onClick={save} disabled={uploading} className="w-full font-semibold" data-testid={`${testId}-save-button`}>Enviar prova</Button>
    </div>
  );
}

function ProofView({ proof, mode, onReview, onResend, testId }) {
  const st = STATUS[proof.status];
  return (
    <div className="space-y-4">
      {proof.photo && <img src={proof.photo} alt="Prova" className="max-h-72 w-full rounded-xl object-cover" data-testid={`${testId}-photo`} />}
      {proof.note && <p className="rounded-lg bg-neutral-50 p-4 text-sm text-neutral-700" data-testid={`${testId}-note`}>{proof.note}</p>}
      <div className="flex items-center justify-between text-xs">
        <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 font-medium ${st.cls}`} data-testid={`${testId}-status`}><st.icon className="h-3 w-3" /> {st.label}</span>
        <span className="text-neutral-400">Enviada em {fmtDate(proof.date)}</span>
      </div>
      {mode === "owner" && proof.status === "pendente" && (
        <div className="grid grid-cols-2 gap-3">
          <Button variant="outline" onClick={() => onReview("ajuste")} data-testid={`${testId}-adjust-button`}>Pedir ajuste</Button>
          <Button onClick={() => onReview("aprovada")} className="font-semibold" data-testid={`${testId}-approve-button`}>Aprovar</Button>
        </div>
      )}
      {mode === "collab" && proof.status === "ajuste" && <Button onClick={onResend} className="w-full font-semibold" data-testid={`${testId}-resend-button`}>Reenviar prova</Button>}
    </div>
  );
}

export function ProofControl({ item, mode, onSave, onReview, testId, requirePhoto, companyId }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const proof = item.proof;
  if (!proof && mode !== "collab") return null;
  const st = proof && STATUS[proof.status];
  const close = () => { setOpen(false); setEditing(false); };
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={testId} className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-colors ${proof ? st.cls : "border-neutral-200 text-neutral-500 hover:border-gold hover:text-gold-dark"}`}>
        {proof ? <st.icon className="h-3 w-3" /> : <Camera className="h-3 w-3" />} {proof ? st.label : "Prova"}
      </button>
      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
        <DialogContent className="w-[calc(100%-2rem)] rounded-xl" data-testid={`${testId}-dialog`}>
          <DialogHeader><DialogTitle className="font-display text-xl">Prova da atividade</DialogTitle></DialogHeader>
          <p className="-mt-2 text-sm text-neutral-500">{item.text}</p>
          {!proof || editing ? (
            <ProofForm initial={proof} testId={testId} requirePhoto={requirePhoto} companyId={companyId} onSave={(p) => { onSave(p); toast.success("Prova enviada para aprovação."); close(); }} />
          ) : (
            <ProofView proof={proof} mode={mode} testId={testId} onResend={() => setEditing(true)} onReview={(s) => { onReview(s); toast.success(s === "aprovada" ? "Prova aprovada." : "Ajuste solicitado."); close(); }} />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
